import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

export type CancellationReasonType =
  | "CUSTOMER_REQUEST"
  | "DOUBLE_BOOKING"
  | "FULLY_BOOKED"
  | "WORKER_UNAVAILABLE"
  | "SALON_CLOSURE"
  | "EMERGENCY"
  | "OTHER";

interface DeclineAppointmentDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reasonType: CancellationReasonType, reasonText: string) => void;
  isLoading?: boolean;
}

export function DeclineAppointmentDialog({
  open,
  onClose,
  onConfirm,
  isLoading = false,
}: DeclineAppointmentDialogProps) {
  const { t } = useTranslation("appointments");
  const [reasonType, setReasonType] = useState<CancellationReasonType | "">("");
  const [reasonText, setReasonText] = useState("");

  const handleConfirm = () => {
    if (!reasonType) return;
    onConfirm(reasonType, reasonText);
  };

  const handleClose = () => {
    if (!isLoading) {
      setReasonType("");
      setReasonText("");
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t("decline.title")}</DialogTitle>
          <DialogDescription>{t("decline.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="reason-type">{t("decline.reasonLabel")}</Label>
            <Select
              value={reasonType}
              onValueChange={(value) => setReasonType(value as CancellationReasonType)}
              disabled={isLoading}
            >
              <SelectTrigger id="reason-type">
                <SelectValue placeholder={t("decline.reasonPlaceholder")}>
                  {reasonType ? t(`decline.reasons.${reasonType}`) : t("decline.reasonPlaceholder")}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CUSTOMER_REQUEST">
                  {t("decline.reasons.CUSTOMER_REQUEST")}
                </SelectItem>
                <SelectItem value="DOUBLE_BOOKING">
                  {t("decline.reasons.DOUBLE_BOOKING")}
                </SelectItem>
                <SelectItem value="FULLY_BOOKED">{t("decline.reasons.FULLY_BOOKED")}</SelectItem>
                <SelectItem value="WORKER_UNAVAILABLE">
                  {t("decline.reasons.WORKER_UNAVAILABLE")}
                </SelectItem>
                <SelectItem value="SALON_CLOSURE">{t("decline.reasons.SALON_CLOSURE")}</SelectItem>
                <SelectItem value="EMERGENCY">{t("decline.reasons.EMERGENCY")}</SelectItem>
                <SelectItem value="OTHER">{t("decline.reasons.OTHER")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason-text">{t("decline.additionalDetailsLabel")}</Label>
            <Textarea
              id="reason-text"
              placeholder={t("decline.additionalDetailsPlaceholder")}
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              disabled={isLoading}
              rows={4}
            />
          </div>
        </div>

        <DialogFooter className="flex-row">
          <Button variant="outline" onClick={handleClose} disabled={isLoading} className="flex-1">
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!reasonType || isLoading}
            variant="destructive"
            className="flex-1"
          >
            {isLoading ? t("decline.declining") : t("decline.confirmDecline")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
